<script setup>
import { computed } from 'vue';
import { useLayout } from '@/layout/admin/composables/layout';

const { layoutConfig } = useLayout();

const logoUrl = computed(() => {
    return `/layout/images/${layoutConfig.darkTheme.value ? 'logo-white' : 'logo-dark'}.png`;
});

const env = import.meta.env.VITE_APP_APP_ENV;
</script>

<template>
    <div
        v-if="!isMobileDevice"
        class="surface-overlay px-3 sm:px-6 flex flex-wrap align-items-stretch justify-content-between lg:border-none surface-border nav-fixed-top lg:shadow-none pb-4">
        <div class="container-xl flex flex-wrap align-items-stretch justify-content-between mt-3">
            <a
                class="cursor-pointer flex align-items-center lg:hidden text-700 mr-3 p-ripple"
                data-pd-ripple="true"
                data-pd-styleclass="true"
                @click="onNavMobileClick($event)">
                <i class="pi pi-bars text-4xl text-primary"></i>
                <span
                    role="presentation"
                    aria-hidden="true"
                    data-p-ink="true"
                    data-p-ink-active="false"
                    class="p-ink"
                    data-pc-name="ripple"
                    data-pc-section="root"></span>
            </a>
            <div
                id="nav-2"
                class="surface-overlay lg:flex items-center absolute lg:static left-0 top-100 z-1 shadow-2 lg:shadow-none w-full lg:w-auto py-2 lg:py-2"
                :class="{ hidden: !navActiveOnMobile }">
                <img
                    :src="$store.state.setting?.image_url ?? logoUrl"
                    alt="Logo"
                    height="50"
                    class="cursor-pointer mr-4"
                    @click="$router.push({ name: 'home' })" />

                <div class="lg:flex w-full lg:w-auto hidden py-3 lg:py-0">
                    <ul
                        class="list-none p-0 m-0 flex w-full justify-content-between align-items-center"
                        style="min-height: 40px">
                        <li class="flex flex-1 justify-content-center">
                            <div class="flex align-items-center gap-2">
                                <div
                                    v-for="(category, index) in categories.slice(0, 5)"
                                    :key="category.id || index"
                                    class="relative"
                                    @mouseenter="showDropdown = category.id"
                                    @mouseleave="showDropdown = null">
                                    <span class="text-900 font-medium cursor-pointer px-3 py-2 hover:text-primary transition-colors">
                                        {{ category.name }}
                                    </span>

                                    <div
                                        v-if="showDropdown === category.id && category.sub_categories && category.sub_categories.length > 0"
                                        class="absolute top-100 left-0 bg-white shadow-3 border-round p-3 z-5"
                                        style="min-width: 200px; margin-top: 0.5rem">
                                        <ul class="list-none p-0 m-0">
                                            <li
                                                v-for="subCategory in category.sub_categories"
                                                :key="subCategory.id">
                                                <router-link
                                                    :to="{ name: 'category-details', params: { slug: slugify(category.name), sub: subCategory.id } }"
                                                    class="flex align-items-center cursor-pointer border-round text-700 hover:surface-100 transition-duration-150 transition-colors px-3 py-2 w-full text-decoration-none"
                                                    @click="onSubCategoryClick(subCategory.name)">
                                                    <span class="font-medium">{{ subCategory.name }}</span>
                                                </router-link>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                                <span
                                    v-if="categories.length > 5"
                                    class="font-medium cursor-pointer px-3 py-2 hover:text-primary transition-colors"
                                    style="color: red"
                                    @click="sidebarVisible = !sidebarVisible">
                                    More
                                </span>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>

            <div class="lg:flex w-full lg:w-auto hidden py-3 lg:py-0">
                <ul
                    class="list-none p-0 m-0 flex w-full justify-content-between align-items-center"
                    style="min-height: 30px">
                    <li class="flex"></li>

                    <li class="flex gap-2">
                        <a
                            class="text-900 font-medium inline-flex align-items-center justify-content-between cursor-pointer px-2 hover:text-primary p-ripple border-1 surface-border border-round"
                            data-pd-ripple="true"
                            @click="onClickSearchButton"
                            style="background: var(--surface-ground); height: 40px; min-width: 195px">
                            <span class="text-500">Search</span>
                            <i class="pi pi-search text-500"></i>
                            <span
                                role="presentation"
                                aria-hidden="true"
                                data-p-ink="true"
                                data-p-ink-active="false"
                                class="p-ink"
                                data-pc-name="ripple"
                                data-pc-section="root"></span>
                        </a>

                        <router-link
                            class="text-900 font-medium inline-flex align-items-center cursor-pointer lg:pl-3 pr-1 hover:text-primary p-ripple"
                            data-pd-ripple="true"
                            :to="{ name: 'article' }">
                            <i
                                class="pi pi-book text-2xl md:text-3xl lg:text-4xl p-overlay-badge"
                                data-pd-badge="true"
                                data-p-overlay-badge="true"></i>
                            <span class="hidden">Article</span>
                            <span
                                role="presentation"
                                aria-hidden="true"
                                data-p-ink="true"
                                data-p-ink-active="false"
                                class="p-ink"
                                data-pc-name="ripple"
                                data-pc-section="root"></span>
                        </router-link>

                        <router-link
                            class="text-900 font-medium inline-flex align-items-center cursor-pointer px-3 hover:text-primary p-ripple"
                            data-pd-ripple="true"
                            :to="{ name: 'cart' }"
                            style="padding-right: 1rem; padding-top: 0.25rem; padding-bottom: 0.25rem">
                            <i
                                class="pi pi-shopping-cart text-2xl md:text-3xl lg:text-4xl p-overlay-badge"
                                data-pd-badge="true"
                                data-p-overlay-badge="true"
                                style="transform: translateY(1px)">
                                <span
                                    id="pv_id_122_badge"
                                    class="p-badge p-component p-badge-dot"
                                    data-pc-name="badge"
                                    data-pc-section="root"></span>
                            </i>
                            <span class="hidden">Cart</span>
                            <span
                                role="presentation"
                                aria-hidden="true"
                                data-p-ink="true"
                                data-p-ink-active="false"
                                class="p-ink"
                                data-pc-name="ripple"
                                data-pc-section="root"></span>
                        </router-link>

                        <router-link
                            class="text-900 font-medium inline-flex align-items-center cursor-pointer px-3 hover:text-primary p-ripple"
                            data-pd-ripple="true"
                            :to="{ name: 'profile' }">
                            <i
                                class="pi pi-user text-2xl md:text-3xl lg:text-4xl"
                                style="transform: translateY(1px)"></i>
                            <span class="hidden">Sign In333</span>
                            <span
                                role="presentation"
                                aria-hidden="true"
                                data-p-ink="true"
                                data-p-ink-active="false"
                                class="p-ink"
                                data-pc-name="ripple"
                                data-pc-section="root"></span>
                        </router-link>
                    </li>
                </ul>
            </div>
        </div>
    </div>

    <div
        v-if="isMobileDevice"
        class="flex w-full lg:w-auto border-y-1 surface-border surface-overlay lg:hidden py-3 lg:py-0 nav-fixed-top">
        <ul
            class="list-none p-0 m-0 flex w-full justify-content-between align-items-center"
            style="min-height: 30px">
            <!-- Logo - stays on the left -->
            <li class="flex">
                <a
                    class="text-900 font-medium inline-flex align-items-center cursor-pointer px-3 hover:text-primary p-ripple"
                    data-pd-ripple="true"
                    @click="$router.push({ name: 'home' })">
                    <img
                        :src="$store.state.setting?.image_url ?? logoUrl"
                        alt="Logo"
                        height="30" />
                    <span class="hidden">Main Home</span>
                    <span
                        role="presentation"
                        aria-hidden="true"
                        data-p-ink="true"
                        data-p-ink-active="false"
                        class="p-ink"
                        data-pc-name="ripple"
                        data-pc-section="root"></span>
                </a>
            </li>

            <li class="flex">
                <a
                    v-if="!isMobileDevice"
                    class="text-900 font-medium inline-flex align-items-center cursor-pointer px-2 hover:text-primary p-ripple border-1 surface-border border-round"
                    data-pd-ripple="true"
                    @click="onClickSearchButton"
                    style="background: var(--surface-ground); height: 35px; min-width: 120px">
                    <i class="pi pi-search text-xs mr-2 text-500"></i>
                    <span class="text-500 text-xs">Product Name</span>
                    <span class="hidden">Search</span>
                    <span
                        role="presentation"
                        aria-hidden="true"
                        data-p-ink="true"
                        data-p-ink-active="false"
                        class="p-ink"
                        data-pc-name="ripple"
                        data-pc-section="root"></span>
                </a>

                <div
                    v-else
                    class="flex items-center border-1 surface-border border-round cursor-pointer"
                    style="background: var(--surface-ground); height: 35px; min-width: 180px; width: 100%; max-width: 250px; position: relative"
                    @click="onClickSearchButton">
                    <input
                        type="text"
                        class="pl-2 text-xs text-500 bg-transparent border-0 outline-none w-full cursor-pointer"
                        style="padding-right: 24px"
                        placeholder="Search Product"
                        readonly />
                    <i
                        class="pi pi-search text-xs text-500"
                        style="position: absolute; right: 6px; top: 50%; transform: translateY(-50%)"></i>
                </div>

                <router-link
                    class="text-900 font-medium inline-flex align-items-center cursor-pointer px-3 hover:text-primary p-ripple"
                    data-pd-ripple="true"
                    :to="{ name: 'cart' }"
                    style="padding-right: 1rem; padding-top: 0.25rem; padding-bottom: 0.25rem">
                    <i
                        class="pi pi-shopping-cart text-xl p-overlay-badge"
                        data-pd-badge="true"
                        data-p-overlay-badge="true"
                        style="transform: translateY(1px)">
                        <span
                            id="pv_id_123_badge"
                            class="p-badge p-component p-badge-dot"
                            data-pc-name="badge"
                            data-pc-section="root"></span>
                    </i>
                    <span class="hidden">Cart</span>
                    <span
                        role="presentation"
                        aria-hidden="true"
                        data-p-ink="true"
                        data-p-ink-active="false"
                        class="p-ink"
                        data-pc-name="ripple"
                        data-pc-section="root"></span>
                </router-link>

                <router-link
                    class="text-900 font-medium inline-flex align-items-center cursor-pointer px-3 hover:text-primary p-ripple"
                    data-pd-ripple="true"
                    :to="{ name: 'profile' }">
                    <i
                        class="pi pi-user text-xl"
                        style="transform: translateY(1px)"></i>
                    <span class="hidden">Sign In</span>
                    <span
                        role="presentation"
                        aria-hidden="true"
                        data-p-ink="true"
                        data-p-ink-active="false"
                        class="p-ink"
                        data-pc-name="ripple"
                        data-pc-section="root"></span>
                </router-link>

                <div
                    class="text-900 font-medium inline-flex align-items-center cursor-pointer px-3 hover:text-primary p-ripple"
                    data-pd-ripple="true"
                    @click="sidebarVisible = !sidebarVisible">
                    <i
                        v-if="!isLoading"
                        class="pi pi-bars text-xl"
                        style="transform: translateY(1px)"></i>
                    <ProgressSpinner
                        v-else
                        style="width: 1.5rem; height: 1.5rem" />
                    <span class="hidden">Categories</span>
                    <span
                        role="presentation"
                        aria-hidden="true"
                        data-p-ink="true"
                        data-p-ink-active="false"
                        class="p-ink"
                        data-pc-name="ripple"
                        data-pc-section="root"></span>
                </div>
            </li>
        </ul>
    </div>

    <Sidebar
        v-model:visible="sidebarVisible"
        header="Product Categories"
        :style="{ width: '400px' }">
        <Accordion v-model:activeIndex="activeIndex">
            <AccordionTab
                v-for="(category, index) in categories"
                :key="category.id || index">
                <template #header>
                    <div class="w-full text-left pl-0 m-0">{{ category.name }}</div>
                </template>
                <ul class="list-none p-0 m-0 overflow-hidden">
                    <li
                        v-for="subCategory in category.sub_categories"
                        :key="subCategory.id">
                        <router-link
                            :to="{ name: 'category-details', params: { slug: slugify(category.name), sub: subCategory.id } }"
                            v-ripple
                            class="flex align-items-center cursor-pointer border-round text-700 hover:surface-100 transition-duration-150 transition-colors p-ripple px-3 py-2 w-full"
                            @click="onSubCategoryClick(subCategory.name)">
                            <span
                                class="font-medium"
                                :class="{ 'font-bold': activeTab === subCategory.name }"
                                >{{ subCategory.name }}</span
                            >
                        </router-link>
                    </li>
                </ul>
            </AccordionTab>
        </Accordion>
    </Sidebar>

    <template v-if="search">
        <AppSearchProduct
            :search="search"
            @closeModal="onCloseModal"></AppSearchProduct>
    </template>
</template>

<script>
import DarkmodeSwitcherComponent from '@/components/_global/DarkmodeSwitcherComponent.vue';
import AppSearchProduct from '@/layout/user/components/utils/AppSearchProduct.vue';

export default {
    name: 'UserAppNavbar',
    data: () => ({
        isMobileDevice: false,
        navActiveOnMobile: false,
        search: false,
        sidebarVisible: false,
        activeIndex: null,
        activeTab: '',
        categories: [],
        isLoading: false,
        showDropdown: null
    }),
    mounted() {
        this.checkMediaQuery();
        this.$nextTick(async () => {
            await this.getCategories();
        });
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.checkMediaQuery);
    },
    methods: {
        async getCategories() {
            this.isLoading = true;
            this.categories = [];
            try {
                var {
                    data: {
                        data: { categories }
                    }
                } = await axios.get('/v2/category');
                const filteredCategories = categories
                    .map((category) => {
                        const filteredSubCategories = category.sub_categories.filter((sub) => sub.products && sub.products.length > 0);

                        const hasProducts = category.products && category.products.length > 0;
                        const hasSubCategoryProducts = filteredSubCategories.length > 0;

                        if (hasProducts || hasSubCategoryProducts) {
                            return {
                                ...category,
                                sub_categories: filteredSubCategories
                            };
                        }

                        return null;
                    })
                    .filter((category) => category !== null);

                this.categories = filteredCategories;
                console.log('FULL RESPONSE:', response);
console.log('RESPONSE DATA:', response.data);
console.log('CATEGORIES:', response.data?.data?.categories);
            } catch ({ response: error }) {
                console.error('error get category', error);
            } finally {
                this.isLoading = false;
            }
        },
        slugify(text) {
            return text
                .toString()
                .toLowerCase()
                .trim()
                .replace(/\s+/g, '-')
                .replace(/[^\w\-]+/g, '')
                .replace(/\-\-+/g, '-');
        },
        onSubCategoryClick(subCategoryName) {
            this.activeTab = subCategoryName;
            this.showDropdown = null;
        },
        onNavMobileClick(event) {
            let barsElement = event.currentTarget.lastElementChild;
            barsElement.classList.add('p-ink-active');
            barsElement.setAttribute('data-p-ink-active', 'true');
            setTimeout(() => {
                barsElement.classList.remove('p-ink-active');
                barsElement.setAttribute('data-p-ink-active', 'false');
            }, 10);

            this.navActiveOnMobile = !this.navActiveOnMobile;
        },
        onClickSearchButton() {
            this.search = !this.search;
        },
        onCloseModal() {
            this.search = false;
        },
        async onSignOut() {
            try {
                await window.axios.get('/v1/auth/logout');
            } catch ({ response: { data } }) {
                if (Object.keys(data).includes('error')) {
                    var {
                        error: { message }
                    } = data;
                } else {
                    var { message } = data;
                }

                console.error(message);
            } finally {
                this.$router.push({ name: 'login' });
            }
        },
        onSignIn() {
            this.$router.push({ name: 'login' });
        },
        checkMediaQuery() {
            this.isMobileDevice = window.matchMedia('(max-width: 768px)').matches;
        }
    },
    components: {
        DarkmodeSwitcherComponent,
        AppSearchProduct
    }
};
</script>

<style scoped>
.nav-fixed-top {
    position: fixed;
    z-index: 999;
    top: 0px;
    right: 0;
    left: 0;
    border-bottom: 1px solid var(--NN50, #f0f3f7);
    transition: transform 280ms ease;
}

@media (max-width: 991px) {
    .nav-fixed-bottom {
        position: fixed;
        z-index: 999;
        bottom: 0px;
        right: 0;
        left: 0;
        border-bottom: 1px solid var(--NN50, #f0f3f7);
        transition: transform 280ms ease;
    }
}
</style>